import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import teamAccessService from '../../services/teamAccessService';
import { rbacService } from '../../services/rbacService';
import { useAuth } from '../../contexts/AuthContext';
import UserManagementTable from './components/UserManagementTable';
import RoleAssignmentModal from './components/RoleAssignmentModal';
import ActivityLogPanel from './components/ActivityLogPanel';
import AccessStatisticsCard from './components/AccessStatisticsCard';
import RoleDistributionChart from './components/RoleDistributionChart';
import realtimeDashboardService from '../../services/realtimeDashboardService';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';

const TeamAccessManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [teamMembers, setTeamMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [roleDistribution, setRoleDistribution] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  useEffect(() => {
    loadData();
    checkSubscription();

    // Subscribe to real-time updates
    const channels = realtimeDashboardService?.subscribeToAllUpdates(
      {
        onViolation: () => loadData(),
        onPayrollError: () => loadData(),
        onDeadline: () => loadData()
      },
      'all'
    );

    return () => {
      realtimeDashboardService?.unsubscribeAll(channels);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [membersData, rolesData, statsData, distributionData] = await Promise.all([
        teamAccessService?.getAllTeamMembers(),
        rbacService?.getAllRoles(),
        teamAccessService?.getAccessStatistics(),
        teamAccessService?.getRoleDistribution()
      ]);

      setTeamMembers(membersData);
      setRoles(rolesData);
      setStatistics(statsData);
      setRoleDistribution(distributionData);

      // Load activity log for active tab
      if (activeTab === 'activity' && activityLog?.length === 0) {
        try {
          const activityData = await teamAccessService?.getUserActivityLog({ limit: 50 });
          setActivityLog(activityData);
        } catch (err) {
          console.error('Error loading activity log:', err);
        }
      }
    } catch (err) {
      setError(err?.message || 'Failed to load team access data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleAssignRole = (member) => {
    setSelectedMember(member);
    setShowRoleModal(true);
  };

  const handleRoleAssignment = async (assignment) => {
    try {
      await teamAccessService?.assignRoleToMember(
        assignment?.userEmail,
        assignment?.roleId,
        assignment?.department,
        user?.email
      );
      setShowRoleModal(false);
      setSelectedMember(null);
      await loadData();
    } catch (err) {
      throw err;
    }
  };

  const handleRevokeRole = async (assignmentId) => {
    if (!confirm('Are you sure you want to revoke this role assignment?')) return;

    try {
      await teamAccessService?.revokeRoleFromMember(assignmentId, user?.email);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to revoke role');
    }
  };

  const handleToggleStatus = async (memberId, currentStatus) => {
    if (!confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this user?`)) return;

    try {
      await teamAccessService?.updateTeamMemberStatus(memberId, !currentStatus);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to update user status');
    }
  };

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    
    if (tab === 'activity' && activityLog?.length === 0) {
      try {
        const activityData = await teamAccessService?.getUserActivityLog({ limit: 50 });
        setActivityLog(activityData);
      } catch (err) {
        console.error('Error loading activity log:', err);
      }
    }
  };

  const tabs = [
    { id: 'users', label: 'User Management', icon: 'Users' },
    { id: 'roles', label: 'Role Distribution', icon: 'Shield' },
    { id: 'activity', label: 'Activity Monitoring', icon: 'Activity' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar onFilterChange={setGlobalFilter} />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />

      <main className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Team Access Management</h1>
            <p className="text-muted-foreground">
              Manage user permissions, role assignments, and monitor access activity for compliance audits
            </p>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <Icon name="RefreshCw" size={16} className={refreshing ? 'animate-spin' : ''} />
              <span className="ml-2">Refresh</span>
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={18} />
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Statistics Overview */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <AccessStatisticsCard
              title="Active Users"
              value={statistics?.uniqueUsers}
              icon="Users"
              trend="neutral"
            />
            <AccessStatisticsCard
              title="Total Access Events"
              value={statistics?.totalAccess}
              icon="Activity"
              trend="neutral"
            />
            <AccessStatisticsCard
              title="Access Grant Rate"
              value={`${statistics?.accessGrantRate}%`}
              icon="CheckCircle"
              trend={statistics?.accessGrantRate >= 95 ? 'up' : 'down'}
            />
            <AccessStatisticsCard
              title="Recent Activity (24h)"
              value={statistics?.recentAccess}
              icon="Clock"
              trend="neutral"
            />
          </div>
        )}

        {/* Tabs */}
        <div className="bg-card rounded-lg shadow-sm border border-border">
          <div className="border-b border-border">
            <div className="flex overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => handleTabChange(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Icon name="Loader2" size={32} className="animate-spin text-primary" />
              </div>
            ) : (
              <>
                {activeTab === 'users' && (
                  <UserManagementTable
                    members={teamMembers}
                    roles={roles}
                    onAssignRole={handleAssignRole}
                    onRevokeRole={handleRevokeRole}
                    onToggleStatus={handleToggleStatus}
                  />
                )}

                {activeTab === 'roles' && (
                  <div>
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-foreground mb-4">Role Distribution</h3>
                      <RoleDistributionChart data={roleDistribution} />
                    </div>
                    <div className="mt-6">
                      <h3 className="text-lg font-semibold text-foreground mb-4">Available Roles</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {roles?.map((role) => (
                          <div
                            key={role?.id}
                            className="bg-muted/50 rounded-lg p-4 border border-border"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-semibold text-foreground">{role?.roleName}</h4>
                              {role?.isSystemRole && (
                                <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                                  System
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{role?.roleDescription}</p>
                            <div className="mt-3 flex items-center gap-2">
                              <Icon
                                name={role?.isActive ? 'CheckCircle' : 'XCircle'}
                                size={14}
                                className={role?.isActive ? 'text-success' : 'text-muted-foreground'}
                              />
                              <span className="text-xs text-muted-foreground">
                                {role?.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'activity' && (
                  <ActivityLogPanel
                    activityLog={activityLog}
                    onRefresh={async () => {
                      const activityData = await teamAccessService?.getUserActivityLog({ limit: 50 });
                      setActivityLog(activityData);
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Role Assignment Modal */}
      {showRoleModal && (
        <RoleAssignmentModal
          member={selectedMember}
          roles={roles}
          onClose={() => {
            setShowRoleModal(false);
            setSelectedMember(null);
          }}
          onSubmit={handleRoleAssignment}
        />
      )}

      <TeaserEditModal isOpen={showRedirectModal} onClose={closeModal} />
    </div>
  );
};

export default TeamAccessManagement;